// TokenManager web — paralelo ao mobile, mas adaptado para a ausencia de
// SecureStore no browser. Acoes seguras (swallow-and-log) — nunca lancam.
//
// Estrategia de armazenamento (CLAUDE.md §Tokens e Auth):
//  - Access token: APENAS em memoria do modulo. Nao persistido em storage.
//    Ao recarregar a pagina, /auth/refresh o reemite a partir do refresh.
//  - Refresh token: sessionStorage por default (zerado quando aba fecha).
//    Toggle "lembrar-me" no login move para localStorage com escopo claro.
//  - Flag `granamoney_remember_me` em localStorage indica qual storage usar
//    no proximo boot (precisamos saber ANTES de tentar buscar o refresh).
//
// Por que nao httpOnly cookie nesta versao:
//  - Backend retorna refresh em JSON (mesmo contrato do mobile).
//  - Migrar para cookie quebraria o app mobile.
//  - Plano de migracao futura documentado em todo/README.md.

import { logger } from '@/shared/lib/logger';
import { storage, type StorageKind } from '@/shared/lib/storage';

const ACCESS_TOKEN_KEY = 'granamoney_access_token';
const REFRESH_TOKEN_KEY = 'granamoney_refresh_token';
const REMEMBER_ME_KEY = 'granamoney_remember_me';

let accessTokenInMemory: string | null = null;

function getRememberMe(): boolean {
  return storage.get(REMEMBER_ME_KEY, 'local') === '1';
}

function refreshStorageKind(): StorageKind {
  return getRememberMe() ? 'local' : 'session';
}

export interface SetTokensOptions {
  /** Quando true, persiste refresh em localStorage (sobrevive a reload + close-tab). */
  remember?: boolean;
}

export const tokenManager = {
  /** Le o access token em memoria (nao consulta storage — access nunca e persistido). */
  getAccessToken(): string | null {
    return accessTokenInMemory;
  },

  /** Le o refresh token do storage apropriado conforme flag remember-me. */
  getRefreshToken(): string | null {
    // Tentar primeiro o storage indicado pela flag; se nao houver, tentar o
    // outro (recuperacao tolerante a flag corrompida ou leitura cruzada
    // de outra aba). Mantem ordem: respeita a preferencia primeiro.
    const primary = refreshStorageKind();
    const fromPrimary = storage.get(REFRESH_TOKEN_KEY, primary);
    if (fromPrimary) return fromPrimary;
    const secondary: StorageKind = primary === 'local' ? 'session' : 'local';
    return storage.get(REFRESH_TOKEN_KEY, secondary);
  },

  // Persiste refresh ANTES do access (paralelo ao mobile): se o processo
  // morre no meio, o estado resultante e "tem refresh, sem access" — o
  // proximo boot falha em /users/me com 401, o interceptor dispara refresh
  // e obtem novo access. Estado inverso seria pior (access valido por 15min
  // sem rota de recuperacao ate expirar).
  setTokens(accessToken: string, refreshToken: string, opts?: SetTokensOptions): void {
    try {
      if (opts?.remember !== undefined) {
        storage.set(REMEMBER_ME_KEY, opts.remember ? '1' : '0', 'local');
      }
      const targetKind = refreshStorageKind();
      // Limpa o storage oposto antes de escrever — evita refresh duplicado
      // (em local + session) quando usuario alterna remember-me.
      const otherKind: StorageKind = targetKind === 'local' ? 'session' : 'local';
      storage.remove(REFRESH_TOKEN_KEY, otherKind);
      storage.set(REFRESH_TOKEN_KEY, refreshToken, targetKind);
      accessTokenInMemory = accessToken;
    } catch (err) {
      logger.auth.error('Failed to store tokens:', err);
    }
  },

  /** Limpa tokens em memoria e em ambos os storages (defensivo). */
  clearTokens(): void {
    try {
      accessTokenInMemory = null;
      storage.remove(REFRESH_TOKEN_KEY, 'session');
      storage.remove(REFRESH_TOKEN_KEY, 'local');
      // Nao limpa REMEMBER_ME_KEY — preferencia do usuario sobrevive a logout
      // (proximo login pode ser auto-marcado conforme escolha previa).
    } catch (err) {
      logger.auth.error('Failed to clear tokens:', err);
    }
  },

  /** Verifica se o usuario marcou "lembrar-me" anteriormente. */
  isRemembered(): boolean {
    return getRememberMe();
  },
};

// Test-only: forcar reset do estado em memoria entre testes.
export const __testOnly__ = {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  REMEMBER_ME_KEY,
  reset(): void {
    accessTokenInMemory = null;
  },
};
